import React from 'react';
import { GenericFormPage } from '../../components/generic';
import { useCall, Call } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const configWithOptions = (() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Client',
          placeholder: 'Select a client (optional)',
          options: [{ value: '', label: 'None' }],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'calledBy') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Called By',
          placeholder: 'Select user',
          options: [],
          apiEndpoint: '/users',
          labelField: 'userDisplayName',
          valueField: 'id' as const,
        };
      }
      if (field.name === 'clientCallTypeId') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Call Type',
          placeholder: 'Select call type',
          options: [],
          apiEndpoint: '/call-type',
          labelField: 'call_type_desc',
          valueField: 'id' as const,
        };
      }
      return field;
    });

    type FormCall = Omit<Call, 'clientId' | 'calledBy' | 'clientCallTypeId'> & {
      clientId?: string | number;
      calledBy?: string | number;
      clientCallTypeId?: string | number;
    };

    const customMapItemToFormData = (item: Call): Partial<FormCall> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        clientId: baseData.clientId ? String(baseData.clientId) : '',
        calledBy: baseData.calledBy ? String(baseData.calledBy) : '',
        clientCallTypeId: baseData.clientCallTypeId ? String(baseData.clientCallTypeId) : '',
      };
    };

    const customGetInitialFormData = (): Partial<FormCall> => {
      return {
        ...getInitialFormData(),
        clientId: '',
        calledBy: '',
        clientCallTypeId: '',
      };
    };

    const convertToNumber = (value: string | number | undefined): number | undefined => {
      if (value === undefined || value === null || String(value) === '') return undefined;
      return Number(value);
    };

    const customCreate = async (data: Partial<FormCall>) => {
      const processedData: Partial<Call> = {
        ...data,
        clientId: convertToNumber(data.clientId),
        calledBy: convertToNumber(data.calledBy),
        clientCallTypeId: convertToNumber(data.clientCallTypeId),
      };
      return await moduleConfig.form.create(processedData);
    };

    const customUpdate = async (id: number, data: Partial<FormCall>) => {
      const processedData: Partial<Call> = {
        ...data,
        clientId: convertToNumber(data.clientId),
        calledBy: convertToNumber(data.calledBy),
        clientCallTypeId: convertToNumber(data.clientCallTypeId),
      };
      return await moduleConfig.form.update(id, processedData);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<Call>,
      mapItemToFormData: customMapItemToFormData as (item: Call) => Partial<Call>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<Call>) => Promise<Call>,
        update: customUpdate as (id: number, data: Partial<Call>) => Promise<Call>,
      },
    };
  })();

  return (
    <GenericFormPage<Call>
      useData={useCall}
      config={configWithOptions}
    />
  );
};

export default Form;
